import { ShareBarLikeCTAWrapper } from 'app/components/CommonStyled/ShareBar/ShareBarLikeCTA/styled';
import { motion } from 'framer-motion';
import styled, { createGlobalStyle } from 'styled-components';
import { BREAKPOINTS } from 'styles/media';

export const ResourcesGlobalStyles = createGlobalStyle`
  :root {
    --resource-detail-nav-width: 145px;
    --resource-detail-nav-left: 48px;
    --resource-detail-nav-space: calc(var(--resource-detail-nav-width) + var(--resource-detail-nav-left));

    --resource-sidebar-width: 240px;
  }
`;

export const ResourceDetailTransitionViewport = styled.div<{ $clipTransition: boolean }>`
  ${({ $clipTransition }) =>
    $clipTransition
      ? `
    position: relative;
    width: 100%;
    min-width: 0;
    overflow-x: clip;
    overflow-y: visible;
  `
      : 'display: contents;'}
`;

export const ResourceOverallWrapper = styled(motion.div)`
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: 55px;

  ${BREAKPOINTS.tablet} {
    flex-direction: column;

    & > div {
      width: 100%;
    }
  }

  ${({ theme }) =>
    theme.isVoting
      ? `
    ${ShareBarLikeCTAWrapper} {
      display: none;
    }
  `
      : null}

  padding-bottom: 24px;
`;

export const ResourceSubmissionStaticEditingWrapper = styled.div`
  width: 100%;
  min-width: 0;
`;

export const ResourceSubmissionEditingWrapper = styled(motion.div).attrs({
  initial: {
    x: '100%',
  },
  animate: {
    x: 0,
  },
  exit: {
    x: '100%',
  },
  transition: {
    duration: 0.2,
    type: 'easeInOut',
    bounce: 0,
  },
})``;

export const ResourcesWrapper = styled(motion.div).attrs({
  exit: {
    width: '0%',
    overflow: 'hidden',
  },
  transition: {
    duration: 0.3,
    type: 'spring',
    bounce: 0,
  },
})`
  width: 50%;

  display: flex;
  flex-direction: column;
  gap: 35px;

  ${BREAKPOINTS.tablet} {
    width: 100%;
  }
`;

export const ResourcesCommentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ResourceContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (min-width: 1024px) and (max-width: 1845px) {
    & iframe,
    [data-id='resource-image-embed'] {
      max-width: calc(100vw - 290px - 96px - 32px) !important;
    }
  }

  position: relative;
`;

export const ResourceShareBarContainer = styled.div`
  width: 100%;

  display: flex;
  justify-content: center;

  margin: 0 auto;
`;

export const ResourceHeaderContainer = styled.div`
  width: 100%;

  display: flex;
  flex-direction: column;
  gap: 11px;
`;

export const ResourceTitleWrapper = styled.div`
  width: 100%;

  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`;

export const ResourceTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const ResourceTitleHeadingContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 3px;
  min-width: 0;
`;

export const ResourceDetailPageLink = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;

  color: inherit;
  text-decoration: none;
  cursor: pointer;
`;

export const ResourceTitleMessageAllContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  padding: 4px 8px;
  cursor: pointer;
`;

export const ResourceTitleMessageAllText = styled.span`
  font-weight: 500;
  font-size: 14px;
  font-style: normal;
  line-height: normal;

  color: var(--color-text);

  text-align: center;
`;

export const ResourceTitle = styled.h2`
  font-weight: 400;
  font-size: 24px;
  line-height: normal;
  color: var(--color-text);

  ${BREAKPOINTS.mobile} {
    font-size: 20px;
  }
`;

export const ResourceSubtitle = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: normal;
  letter-spacing: 1.4px;
  text-transform: uppercase;

  color: rgba(23, 28, 51, 0.5);
`;

export const ResourceClientTitleContainer = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
`;

export const ResourceClientTitle = styled.h5`
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;
  letter-spacing: 1.6px;
  text-transform: uppercase;
  color: rgba(23, 28, 51, 0.5);
`;

export const ResourceDescription = styled.div`
  * {
    font-weight: 400;
    font-size: 18px;
    line-height: normal;
    color: rgba(23, 28, 51, 0.5);

    ${BREAKPOINTS.mobile} {
      font-size: 16px;
    }
  }
`;

interface ResourceHeaderImageProps {
  size?: 'small' | 'medium' | 'large';
}

export const ResourceHeaderImage = styled.img<ResourceHeaderImageProps>`
  max-width: 100%;

  ${({ size = 'small' }) => {
    switch (size) {
      case 'small':
        return `
          width: 700px;
          height: 400px;
        `;
      case 'medium':
        return `
          width: 900px;
          height: 600px;
        `;
      case 'large':
        return `
          width: 1440px;
          height: 700px;
        `;
      default:
        return `
          width: 700px;
          height: 400px;
        `;
    }
  }}
  object-fit: cover;
`;

// GO BACK
export const ResourceBackContainer = styled.div`
  position: absolute;
  left: 16px;
  top: 40px;

  display: flex;
  flex-direction: row;

  & > :not(:last-child) {
    margin-right: 7px;
  }

  & > .right-arrow-icon {
    transform: rotate(180deg);
  }

  ${BREAKPOINTS.mobile} {
    top: 25.5px;
    left: 16px;
  }

  cursor: pointer;
`;

export const ResourceBackText = styled.span`
  font-weight: 500;
  font-size: 16px;
  line-height: 20px;

  color: rgba(23, 28, 51, 0.5);
  letter-spacing: 1.6px;
  text-transform: uppercase;
`;

export const ResourceImagePreviewWrapper = styled(motion.div)<{ showingEmbed: boolean }>`
  width: 50%;

  display: flex;
  flex-direction: column;
  gap: 40px;

  position: sticky;
  top: ${({ theme }) => (theme.inDetailPage ? '16px' : 'calc(var(--navbar-height) + 16px)')};

  ${BREAKPOINTS.tablet} {
    width: 100%;
    max-height: unset;

    position: relative;
    top: 0;
  }

  overflow-x: hidden;
`;

export const ResourceImage = styled(motion.img)`
  max-width: 100%;
  max-height: 100%;

  object-fit: cover;
`;

// Sits above its loading state so a slow embed shows as soon as it paints anything.
export const ResourceIframe = styled(motion.iframe)<{
  $whiteBackgroundFallback?: boolean;
}>`
  position: relative;
  z-index: 1;
  width: 100%;
  height: 100%;
  flex: 1;
  background: ${({ $whiteBackgroundFallback }) =>
    $whiteBackgroundFallback ? '#ffffff' : 'transparent'};
`;

export const ResourceEmbedPreview = styled.div`
  width: 100%;
  height: 100%;
  flex: 1;

  position: relative;
  display: flex;
`;

export const ResourceEmbedLoadingState = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;

  display: flex;
  align-items: center;
  justify-content: center;

  color: var(--color-primary);
  background: #f4f4f4;
  font-size: 14px;
  font-weight: 500;
`;

// CTA
export const ResourceCTAButtonContainer = styled.div`
  z-index: 2;
  display: flex;
  gap: 12px;
`;

export const ResourceCTAButton = styled.button<{ disabledState: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;

  & > :not(:last-child) {
    margin-right: 10px;
    ${BREAKPOINTS.mobile} {
      margin-right: 0px;
    }
  }

  padding: 16px 32px;
  border-radius: 100px;
  border: 2px solid var(--color-primary);
  background: #ffffff;

  ${BREAKPOINTS.mobile} {
    padding: 8px;
  }

  ${({ disabledState }) =>
    disabledState
      ? `
    opacity: 0.5;
    cursor: not-allowed;
  `
      : null}

  position: relative;
`;

export const ResourceCTAButtonPaywallContent = styled.div`
  width: calc(100% - 32px);
  position: absolute;
  bottom: 0px;
  left: 0;
  right: 0;

  display: flex;
  flex-direction: row;
  align-items: center;

  padding: 2px 8px 4px 8px;
  margin: 0 auto;

  & > svg {
    min-width: 10px;
    min-height: 10px;
  }

  & > :not(:last-child) {
    margin-right: 4px;
    ${BREAKPOINTS.mobile} {
      margin-right: 0px;
    }
  }

  transform: translateY(100%);

  ${BREAKPOINTS.mobile} {
    width: calc(100% - 16px);
  }

  border-radius: 0px 0px 8px 8px;
  background: var(--color-data-company-background-secondary);
`;

export const ResourceCTAButtonPaywallContentText = styled.span`
  font-weight: 400;
  font-size: 12px;
  line-height: normal;
  color: var(--color-data-company-foreground);

  ${BREAKPOINTS.mobile} {
    display: none;
  }
`;

export const ResourceCTAButtonText = styled.span`
  font-weight: 700;
  font-size: 14px;
  line-height: normal;

  color: var(--color-text);
  text-align: center;

  ${BREAKPOINTS.mobile} {
    display: none;
  }
`;

// STICKY CTA
interface ResourceCTAStickyButtonContainerProps {
  show: boolean;
}

export const ResourceCTAStickyButtonContainer = styled.div<ResourceCTAStickyButtonContainerProps>`
  position: sticky;
  top: 50%;

  display: none;

  margin-bottom: 32px;

  transition: all 0.3s ease-in-out;
  ${({ show }) =>
    show
      ? `
    transform: translateX(0%);
    display: flex;
  `
      : `
    transform: translateX(100%);
  `}

  ${BREAKPOINTS.mobile} {
    display: none;
  }

  pointer-events: none;
`;

export const ResourceCTAStickyButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;

  margin-left: auto;

  & > :not(:last-child) {
    margin-right: 10px;
  }

  padding: 16px 24px;
  border-radius: 100px 0px 0px 100px;
  background: #ffffff;
  box-shadow: 0px 4px 30px -10px rgba(0, 0, 0, 0.25);

  position: relative;

  pointer-events: all;
`;

// TAGS
export const ResourceTagsContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 4px;

  ${BREAKPOINTS.mobile} {
    padding: 0px 16px;
  }
`;

export const ResourceTagsChip = styled.span`
  font-weight: 500;
  font-size: 14px;
  line-height: normal;

  padding: 4px 8px;

  color: #323f5d;
  border-radius: 26px;
  border: 1px solid #323f5d;
`;

// LONG DESCRIPTION
export const ResourceLongDescriptionContainer = styled.div`
  width: 100%;
  line-height: 30px;
`;

export const ResourceLongDescription = styled.div`
  & p {
    font-weight: 400;
    font-size: 18px;
    line-height: 30px;
    color: var(--color-text);
  }
`;

// CASE STUDY DISCLAIMER WRAPPER
export const ResourceCaseStudyDisclaimerWrapper = styled.div`
  width: calc(100% - 32px);

  position: absolute;
  top: 32px;
  left: 16px;

  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

// IMAGE PREVIEW
interface ResourceImageContainerProps {
  isEmbedExpanded: boolean;
}

export const ResourceImageContainer = styled.div<ResourceImageContainerProps>`
  height: ${({ isEmbedExpanded }) => (isEmbedExpanded ? '100%' : '45rem')};
  max-height: ${({ isEmbedExpanded }) => (isEmbedExpanded ? 'unset' : '60svh')};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  padding: 140px 8px 16px 8px;

  ${BREAKPOINTS.tablet} {
    padding: 140px 0px 16px 0px;
  }

  border-radius: 8px;
  background: rgba(23, 28, 51, 0.5);
  box-shadow: 0px 6px 24px 0px rgba(0, 0, 0, 0.1);
`;

export const ResourceImagePreviewContainer = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: row;
  gap: 12px;
  padding: 4px;
  overflow-x: auto;
  scroll-padding-inline: 4px;
  scrollbar-width: thin;
`;

export const ResourceImagePreviewThumbnailButton = styled.button<{ $active: boolean }>`
  position: relative;
  flex: 0 0 128px;
  width: 128px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 6px;
  padding: 5px;
  border: 2px solid ${({ $active }) => ($active ? 'var(--color-primary)' : '#d9dce3')};
  border-radius: 12px;
  background: ${({ $active }) => ($active ? '#fff3c4' : '#ffffff')};
  color: var(--color-primary);
  font: inherit;
  cursor: pointer;
  box-sizing: border-box;

  &:hover {
    border-color: var(--color-primary);
  }

  &:focus-visible {
    outline: 2px solid var(--color-primary);
    outline-offset: 2px;
  }
`;

export const ResourceImagePreviewThumbnail = styled.img`
  display: block;
  width: 100%;
  height: 68px;
  object-fit: cover;
  border-radius: 6px;
  background: #f0f1f4;
`;

export const ResourceImagePreviewThumbnailNotImage = styled.div`
  width: 100%;
  height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  background: #f0f1f4;
`;

export const ResourceImagePreviewThumbnailLabel = styled.span`
  display: block;
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ResourceImagePreviewThumbnailSelected = styled.span`
  position: absolute;
  top: 9px;
  right: 9px;
  width: 22px;
  height: 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid #ffffff;
  border-radius: 50%;
  background: var(--color-primary);
  pointer-events: none;
`;

// PERMISSIONS DISCLAIMER
export const ResourcePermissionsDisclaimerWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 4px;

  padding: 5px 10px;

  border-radius: 8px;
  background: #000;
`;

export const ResourcePermissionsDisclaimerTextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const ResourcePermissionsDisclaimerText = styled.span`
  font-weight: 400;
  font-size: 14px;
  line-height: normal;

  color: #ffffff;
`;
